// ============================================================================
// BarChart — animated horizontal bars for ranked values (strength PRs).
// Each bar grows from 0 to its share of the max on scroll-into-view, with a
// gradient fill and a value label. Reduced-motion aware. Horizontal layout
// keeps long exercise names readable on a 390px phone.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { fmtNum } from "../format";
import type { ChartAccent } from "./LineChart";

const ACCENT_BG: Record<ChartAccent, string> = {
  coral: "bg-grad-coral",
  violet: "bg-grad-violet",
  ocean: "bg-grad-ocean",
  lime: "bg-grad-lime",
};

export interface BarDatum {
  label: string;
  value: number;
}

export interface BarChartProps {
  data: BarDatum[];
  accent?: ChartAccent;
  decimals?: number;
  unit?: string;
  locale?: string;
}

/** A list of animated horizontal bars, ranked, sharing one scale. */
export function BarChart({
  data,
  accent = "coral",
  decimals = 1,
  unit = "",
  locale,
}: BarChartProps) {
  const reduce = useReducedMotion();
  const max = Math.max(...data.map((d) => d.value), 1);

  return (
    <ul className="space-y-3">
      {data.map((d, i) => {
        const pct = Math.max(6, (d.value / max) * 100);
        return (
          <li key={`${d.label}-${i}`} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate font-display text-sm font-bold text-body">
                {d.label}
              </span>
              <span className="nums shrink-0 font-display text-sm font-extrabold tracking-tight text-body">
                {fmtNum(d.value, decimals, locale)}
                {unit}
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-pill bg-surface-sunken">
              <motion.div
                className={"h-full rounded-pill " + ACCENT_BG[accent]}
                initial={reduce ? { width: `${pct}%` } : { width: 0 }}
                whileInView={{ width: `${pct}%` }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{
                  duration: reduce ? 0 : 0.9,
                  delay: reduce ? 0 : 0.06 * i,
                  ease: [0.22, 0.61, 0.36, 1],
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
